import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  X,
  ChevronLeft,
  ChevronRight,
  AppWindow,
  HelpCircle,
  Twitch,
  Monitor,
  ChevronDown,
  User,
  Briefcase,
  Bot,
  LayoutDashboard,
  Linkedin,
  Crown,
  Settings,
  ClipboardCheck,
  Users,
  TrendingUp,
  ShieldAlert,
  MessageSquare,
  CircleDot,
} from 'lucide-react';
import HelpSupportModal from './HelpSupportModal';
import { useUserId, useUserEmail } from '../context/UserAuthContext';
import { usePageVisibility } from '../context/PageVisibilityContext';
import { PAGE_SECTIONS } from '../config/pages';

const Sidebar = ({ isOpen = false, setIsOpen = () => {}, isCollapsed = false, setIsCollapsed = () => {} }) => {
  const location = useLocation();
  const userId = useUserId();
  const userEmail = useUserEmail();
  const { isHidden } = usePageVisibility();
  const [showHelpModal, setShowHelpModal] = useState(false);
  
  // View mode for specific users (Personal, Business, Assistant — or for
  // counterfind@operatr.com a role picker: Executive / Operations / Review /
  // Client / Sales). The selected key is namespaced per-user in localStorage
  // so different accounts on the same browser don't clobber each other.
  const isCounterfind = userEmail?.toLowerCase() === 'counterfind@operatr.com';

  // Users who get the view mode dropdown
  const viewModeUsers = ['darren@cresa.com', 'dwoodson@cresa.com', 'darren@operatr.com', 'counterfind@operatr.com'];
  const hasViewModeDropdown = viewModeUsers.includes(userEmail?.toLowerCase());

  const defaultViewMode = isCounterfind ? 'executive' : 'personal';

  const [viewMode, setViewMode] = useState(() => {
    const saved = localStorage.getItem(`sidebar_view_mode_${userEmail?.toLowerCase()}`);
    return saved || defaultViewMode;
  });
  const [showViewDropdown, setShowViewDropdown] = useState(false);
  
  // Save view mode to localStorage
  useEffect(() => {
    if (hasViewModeDropdown && userEmail) {
      localStorage.setItem(`sidebar_view_mode_${userEmail.toLowerCase()}`, viewMode);
    }
  }, [viewMode, userEmail, hasViewModeDropdown]);
  
  // View mode configurations - which pages to show in each view
  // Business view also surfaces /business-dashboard for darren@operatr.com
  // (the entry itself is gated by user-specific menu items below).
  const standardViewModeConfig = {
    personal: {
      label: 'Personal',
      icon: User,
      description: 'All pages',
      allowedPaths: null // null means show all pages
    },
    business: {
      label: 'Business',
      icon: Briefcase,
      description: 'Dashboard, Tasks, Calendar, Meetings',
      allowedPaths: ['/business-dashboard', '/tasks', '/calendar', '/projects-re']
    },
    assistant: {
      label: 'Assistant',
      icon: Bot,
      description: 'Essential work tools',
      allowedPaths: ['/communications-re', '/contacts-re', '/calendar', '/tasks', '/documents-re', '/passwords', '/travel', '/projects-re']
    }
  };

  // Role picker for counterfind@operatr.com. All roles currently show every
  // page (allowedPaths: null) — the picker is purely a role-context switch.
  // We can wire per-role page restrictions in here later without touching
  // the dropdown UI.
  const counterfindRoleConfig = {
    executive: {
      label: 'Executive',
      icon: Crown,
      description: 'Leadership overview',
      allowedPaths: null
    },
    operations: {
      label: 'Operations',
      icon: Settings,
      description: 'Day-to-day execution',
      allowedPaths: null
    },
    review: {
      label: 'Review',
      icon: ClipboardCheck,
      description: 'Audit & approvals',
      allowedPaths: null
    },
    client: {
      label: 'Client',
      icon: Users,
      description: 'Client-facing tools',
      allowedPaths: null
    },
    sales: {
      label: 'Sales',
      icon: TrendingUp,
      description: 'Pipeline & outreach',
      allowedPaths: null
    }
  };

  const viewModeConfig = isCounterfind ? counterfindRoleConfig : standardViewModeConfig;

  // Hard-coded per-user hidden pages (legacy)
  const hiddenPagesForUser = {
    'kalliestockton@gmail.com': ['/properties']
  };
  const legacyHidden = hiddenPagesForUser[userEmail?.toLowerCase()] || [];

  // User-specific label overrides (path -> new label)
  const labelOverridesForUser = {
    'dwoodson@cresa.com': {
      '/projects-re': 'Meetings'
    },
    'darren@cresa.com': {
      '/projects-re': 'Meetings'
    },
    'darren@operatr.com': {
      '/projects-re': 'Meetings'
    },
    'counterfind@operatr.com': {
      '/projects-re': 'Meetings'
    }
  };
  const labelOverrides = labelOverridesForUser[userEmail?.toLowerCase()] || {};

  // User-specific extra items (appended at end of menu)
  const userSpecificMenuItems = {
    'kalliestockton@gmail.com': [
      { icon: null, label: 'STREAMING', path: null, isCategory: true },
      { icon: Twitch, label: 'Twitch', path: '/twitch' },
      { icon: Monitor, label: 'OBS', path: 'https://kallie-creatr.com', isExternal: true }
    ]
  };
  const additionalMenuItems = userSpecificMenuItems[userEmail?.toLowerCase()] || [];

  // Per-user extra entry: Business Dashboard for darren@operatr.com,
  // surfaced only when Business view is active (keeps Personal uncluttered).
  const businessOnlyExtras = [];
  if (userEmail?.toLowerCase() === 'darren@operatr.com' && viewMode === 'business') {
    businessOnlyExtras.push(
      { icon: null, label: 'OVERVIEW', path: null, isCategory: true },
      { icon: LayoutDashboard, label: 'Dashboard', path: '/business-dashboard' }
    );
  }

  // Build flat menu from PAGE_SECTIONS, injecting category headers and applying label overrides.
  // Honour the `userOnly` flag on individual pages so tenant-specific entries
  // (e.g. counterfind's Product Queue) never show up for other accounts.
  // `userExcluded` is the inverse — an array of emails that should NOT see
  // the page (used for "everyone except counterfind" style exclusions).
  const me = userEmail?.toLowerCase();
  const menuItems = PAGE_SECTIONS.flatMap(section => {
    const pages = section.pages.filter(p => {
      if (p.userOnly && p.userOnly.toLowerCase() !== me) return false;
      if (Array.isArray(p.userExcluded) && p.userExcluded.map(e => e.toLowerCase()).includes(me)) return false;
      return true;
    });
    if (pages.length === 0) return [];
    return [
      { icon: null, label: section.label, path: null, isCategory: true },
      ...pages.map(page => ({
        ...page,
        label: labelOverrides[page.path] || page.label,
      })),
    ];
  });

  // Per-user extra: insert "LinkedIn" right after the Meetings (Projects) tab
  // for darren@operatr.com. Opens his profile in a new tab.
  if (userEmail?.toLowerCase() === 'darren@operatr.com') {
    const meetingsIdx = menuItems.findIndex(it => it.path === '/projects-re');
    if (meetingsIdx >= 0) {
      menuItems.splice(meetingsIdx + 1, 0, {
        icon: Linkedin,
        label: 'LinkedIn',
        path: 'https://www.linkedin.com/in/darrenwoodson/',
        isExternal: true,
      });
    }
  }

  // Per-user extra: insert "Slack" and "HubSpot" right after Communications
  // for jaden@gmail.com only.
  if (userEmail?.toLowerCase() === 'jaden@gmail.com') {
    const commsIdx = menuItems.findIndex(it => it.path === '/communications-re');
    if (commsIdx >= 0) {
      menuItems.splice(commsIdx + 1, 0,
        { icon: MessageSquare, label: 'Slack', path: '/slack' },
        { icon: CircleDot, label: 'HubSpot', path: '/hubspot' },
        { icon: ClipboardCheck, label: 'Onboarding', path: '/onboarding-admin' }
      );
    }
  }

  const isActive = (path) => {
    if (!path) return false;
    if (path === '/') return location.pathname === '/';
    const norm = location.pathname.replace(/\/$/, '');
    const check = path.replace(/\/$/, '');
    // Exact match only - don't highlight parent paths when on child routes
    // Exception: allow startsWith for paths that are meant to be parent containers
    // but NOT for /ae/admin which has dedicated child pages
    if (check === '/ae/admin') {
      return norm === check; // Exact match only for AE admin dashboard
    }
    return norm === check || norm.startsWith(check + '/');
  };

  // Filter: remove legacy-hidden and user-toggled-off pages
  // Also filter by view mode for specific users
  // Never hide categories (they're filtered out if all children are hidden)
  const visibleItems = [...businessOnlyExtras, ...menuItems, ...additionalMenuItems].filter(item => {
    if (item.isCategory) return true;
    if (!item.path || item.isExternal) return true;
    if (legacyHidden.includes(item.path)) return false;
    if (isHidden(item.path)) return false;
    
    // Filter userOnly pages - only show to specific users
    if (item.userOnly) {
      if (!userEmail || userEmail.toLowerCase() !== item.userOnly.toLowerCase()) {
        return false;
      }
    }
    
    // Apply view mode filtering for specific users.
    // Skip when the active mode has allowedPaths=null (means "show everything").
    if (hasViewModeDropdown) {
      const config = viewModeConfig[viewMode];
      if (config?.allowedPaths && !config.allowedPaths.includes(item.path)) {
        return false;
      }
    }
    
    return true;
  });

  // Remove orphan category headers (category with no visible page children after it)
  const filteredItems = visibleItems.filter((item, idx, arr) => {
    if (!item.isCategory) return true;
    // Check if next item exists and is not a category
    const next = arr[idx + 1];
    return next && !next.isCategory;
  });

  return (
    <>
      {isOpen && (
        <div className="fixed inset-0 bg-transparent z-40 md:hidden" onClick={() => setIsOpen(false)} />
      )}

      <div className={`h-screen fixed left-0 top-0 overflow-y-auto z-[60] transition-all duration-300 scrollbar-hide bg-[#000000] ${
        isCollapsed ? 'w-20' : 'w-64'
      } ${isOpen ? 'translate-x-0' : '-translate-x-full'} md:translate-x-0`}>

        {/* Mobile close */}
        <div className="md:hidden flex justify-end p-4">
          <button onClick={() => setIsOpen(false)} className="p-2 text-white hover:text-gray-300 transition-colors">
            <X size={24} />
          </button>
        </div>

        {/* Logo */}
        <div className={`pb-4 px-4 ${isCollapsed ? 'px-2' : ''} ${isOpen ? 'pt-0' : 'pt-6'} md:pt-6`}>
          <Link to="/" className="flex items-center justify-center">
            {isCollapsed ? (
              <div
                className="w-10 h-10 bg-white rounded-full flex items-center justify-center text-black font-bold text-xl"
                data-testid="sidebar-logo-collapsed"
              >
                {isCounterfind ? 'C' : 'D'}
              </div>
            ) : isCounterfind ? (
              <img
                src="/branding/counterfind-logo.png"
                alt="CounterFind"
                className="w-full h-auto max-w-[200px]"
                data-testid="sidebar-logo-counterfind"
              />
            ) : (
              <img
                src="https://customer-assets.emergentagent.com/job_dashboard-hub-39/artifacts/cs5395nz_Blue%20and%20White%20Modern%20Medical%20Report%20Dashboard%20%286%29.png"
                alt="DW Logo"
                className="w-full h-auto max-w-[200px]"
              />
            )}
          </Link>
        </div>
        
        {/* View Mode Dropdown - Only for specific users */}
        {hasViewModeDropdown && !isCollapsed && (
          <div className="px-4 pb-4 pt-8">
            <div className="relative">
              <button
                onClick={() => setShowViewDropdown(!showViewDropdown)}
                className="w-full flex items-center justify-between px-4 py-3 bg-black border border-white rounded-xl text-white hover:bg-gray-900 transition-colors"
                data-testid="view-mode-dropdown"
              >
                <div className="flex items-center gap-3">
                  {(() => {
                    const Icon = viewModeConfig[viewMode]?.icon || User;
                    return <Icon size={18} className="text-white" />;
                  })()}
                  <span className="text-sm font-medium">{viewModeConfig[viewMode]?.label || 'Personal'}</span>
                </div>
                <ChevronDown 
                  size={16} 
                  className={`text-white transition-transform duration-200 ${showViewDropdown ? 'rotate-180' : ''}`} 
                />
              </button>
              
              {/* Dropdown Menu */}
              {showViewDropdown && (
                <>
                  <div 
                    className="fixed inset-0 z-10" 
                    onClick={() => setShowViewDropdown(false)} 
                  />
                  <div className="absolute top-full left-0 right-0 mt-2 bg-black border border-white rounded-xl overflow-hidden shadow-xl z-20">
                    {Object.entries(viewModeConfig).map(([key, config]) => (
                      <button
                        key={key}
                        onClick={() => {
                          setViewMode(key);
                          setShowViewDropdown(false);
                        }}
                        className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors border-b border-white/20 last:border-b-0 ${
                          viewMode === key 
                            ? 'bg-white text-black' 
                            : 'text-white hover:bg-white/10'
                        }`}
                        data-testid={`view-mode-${key}`}
                      >
                        <config.icon size={18} className={viewMode === key ? 'text-black' : 'text-white'} />
                        <div>
                          <div className="text-sm font-medium">{config.label}</div>
                          <div className={`text-xs ${viewMode === key ? 'text-gray-600' : 'text-gray-400'}`}>{config.description}</div>
                        </div>
                        {viewMode === key && (
                          <div className="ml-auto w-2 h-2 bg-black rounded-full" />
                        )}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        )}
        
        {/* View Mode Indicator - Collapsed state */}
        {hasViewModeDropdown && isCollapsed && (
          <div className="px-2 pb-4 pt-8">
            <button
              onClick={() => {
                const keys = Object.keys(viewModeConfig);
                const idx = keys.indexOf(viewMode);
                const next = keys[(idx + 1) % keys.length];
                setViewMode(next);
              }}
              className="w-full flex items-center justify-center p-3 bg-black border border-white rounded-xl text-white hover:bg-gray-900 transition-colors"
              title={`Current: ${viewModeConfig[viewMode]?.label}. Click to switch.`}
              data-testid="view-mode-cycle-collapsed"
            >
              {(() => {
                const Icon = viewModeConfig[viewMode]?.icon || User;
                return <Icon size={18} className="text-white" />;
              })()}
            </button>
          </div>
        )}

        {/* Menu */}
        <div className="pt-8 pb-24">
          <div className="space-y-1">
            {filteredItems.map((item, idx) =>
              item.isCategory ? (
                <div key={idx}
                  className={`flex items-center gap-3 ${isCollapsed ? 'justify-center px-3 py-2 mx-2' : 'px-6 py-2 mx-3'} text-gray-500 ${idx === 0 ? 'mt-0' : 'mt-8 pt-6'}`}>
                  {!isCollapsed && <span className="text-xs font-semibold tracking-wider uppercase">{item.label}</span>}
                </div>
              ) : item.isExternal ? (
                <a key={idx} href={item.path} target="_blank" rel="noopener noreferrer" onClick={() => setIsOpen(false)}
                  data-testid={`sidebar-external-${(item.label || '').toLowerCase().replace(/\s+/g, '-')}`}
                  className={`flex items-center gap-3 ${isCollapsed ? 'justify-center px-3 py-3 mx-2' : 'px-6 py-3 mx-3'} rounded-lg transition-all duration-200 text-white hover:bg-white/10`}>
                  {item.icon && <item.icon size={20} />}
                  {!isCollapsed && <span className="text-sm font-medium">{item.label}</span>}
                </a>
              ) : (
                <Link key={idx} to={item.path} onClick={() => setIsOpen(false)}
                  className={`flex items-center gap-3 ${isCollapsed ? 'justify-center px-3 py-3 mx-2' : 'px-6 py-3 mx-3'} rounded-lg transition-all duration-200 ${
                    isActive(item.path) ? 'bg-white text-black' : 'text-white hover:bg-white/10'
                  }`}>
                  {item.icon && <item.icon size={20} />}
                  {!isCollapsed && <span className="text-sm font-medium">{item.label}</span>}
                </Link>
              )
            )}
          </div>

          {/* Collapse toggle */}
          <div className="mt-8 mb-4 px-3">
            <button onClick={() => setIsCollapsed(!isCollapsed)}
              className={`hidden md:flex w-full items-center gap-2 px-4 py-3 bg-white rounded-xl text-black hover:bg-gray-100 transition-colors ${isCollapsed ? 'justify-center' : ''}`}>
              {isCollapsed ? <ChevronRight size={18} /> : <><ChevronLeft size={18} /><span className="text-sm font-medium">Collapse</span></>}
            </button>
          </div>

          {/* Help */}
          <div className="px-3 mt-2">
            <button onClick={() => setShowHelpModal(true)}
              className={`w-full flex items-center gap-2 px-4 py-3 bg-gradient-to-r from-purple-600 to-pink-600 rounded-xl text-white hover:from-purple-700 hover:to-pink-700 transition-all ${isCollapsed ? 'justify-center' : ''}`}
              data-testid="help-support-btn">
              <HelpCircle size={18} />
              {!isCollapsed && <span className="text-sm font-medium">Help & Support</span>}
            </button>
          </div>
        </div>
      </div>

      <HelpSupportModal isOpen={showHelpModal} onClose={() => setShowHelpModal(false)}
        userId={userId} userEmail="" userName="" />
    </>
  );
};

export default Sidebar;

